import { useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useBackNavigate } from '@/shared/hooks/use-back-navigate';
import {
  Badge,
  Button,
  Select,
  SurfaceCard,
  PageContainer,
} from '@react-apps/design-system';
import {
  PRODUCT_STATUSES,
  type Product,
  type ProductStatus,
} from '../../domain/entities/catalog.entity';
import {
  useUpdateProductExternalKey,
  useUpdateProductStatus,
} from '../hooks/use-products';
import { useProductPerks } from '../hooks/use-product-perks';
import { useAllPerkGroups } from '../hooks/use-perks';
import {
  CATALOG_PROVIDER_LABELS,
  DELIVERS_PER_CYCLE_LABELS,
  GRANT_POLICY_LABELS,
  OVERDUE_POLICY_LABELS,
  PRODUCT_STATUS_BADGE,
  PRODUCT_STATUS_LABELS,
  msToDays,
} from '../labels';
import {
  EMPTY_PRODUCT_EXTERNAL_KEY,
  isProductExternalKeyComplete,
  ProductExternalKeyFields,
  productExternalKeyEquals,
  trimProductExternalKey,
  type ProductExternalKeyFormValue,
} from '../components/ProductExternalKeyFields';
import { ProductPerksEditorModal } from '../components/ProductPerksEditorModal';
import { ScopeBadgeList } from '@/shared/scopes/ScopeBadge';

function Card({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <SurfaceCard className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-(--color-text-primary) text-sm font-semibold">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </SurfaceCard>
  );
}

/**
 * A chave vem do back com as quatro partes nulas enquanto o produto é rascunho;
 * o formulário trabalha com strings vazias e o gateway default do combo.
 */
function toFormValue(product?: Product): ProductExternalKeyFormValue {
  if (!product) return EMPTY_PRODUCT_EXTERNAL_KEY;
  return {
    provider: product.provider ?? EMPTY_PRODUCT_EXTERNAL_KEY.provider,
    providerAccountId: product.providerAccountId ?? '',
    productExternalId: product.productExternalId ?? '',
    offerExternalId: product.offerExternalId ?? '',
  };
}

export function ProductDetailPage() {
  const { productId } = useParams<{ productId: string }>();
  const goBack = useBackNavigate('/management/catalog');
  const location = useLocation();
  // Não há GET /admin/products/:id no back — o produto vem via state da navegação.
  const product = (location.state as { product?: Product } | null)?.product;

  const [status, setStatus] = useState<ProductStatus>(
    product?.status ?? 'missing_config',
  );
  const [externalKey, setExternalKey] = useState<ProductExternalKeyFormValue>(
    toFormValue(product),
  );
  const [isPerksEditorOpen, setIsPerksEditorOpen] = useState(false);

  const updateStatus = useUpdateProductStatus(productId ?? '');
  const updateExternalKey = useUpdateProductExternalKey(productId ?? '');
  const { data: perks, isLoading: perksLoading } = useProductPerks(
    productId ?? '',
  );
  const { data: perkGroups } = useAllPerkGroups();

  // O perkId identifica o par (capacidade, forma de concessão) — só o catálogo
  // agrupado sabe traduzir de volta para key + kind.
  const perkLabelById = useMemo(() => {
    const map = new Map<string, { key: string; kind: string }>();
    for (const group of perkGroups ?? []) {
      for (const form of group.grantForms) {
        map.set(form.perkId, {
          key: group.key,
          kind: GRANT_POLICY_LABELS[form.grantPolicyKind],
        });
      }
    }
    return map;
  }, [perkGroups]);

  const statusDirty = product ? status !== product.status : true;
  const externalKeyDirty = product
    ? !productExternalKeyEquals(externalKey, toFormValue(product))
    : false;
  const canEditExternalKey = (product?.status ?? status) === 'missing_config';

  return (
    <PageContainer maxWidth="4xl">
      <div className="flex items-center gap-4">
        <button
          onClick={goBack}
          className="text-(--color-text-secondary) hover:text-(--color-text-primary) flex items-center gap-1.5 text-sm transition-colors"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 19.5 8.25 12l7.5-7.5"
            />
          </svg>
          Catálogo
        </button>
      </div>

      {/* Header */}
      <SurfaceCard className="flex flex-wrap items-center gap-4">
        <div className="flex-1 space-y-1">
          <h1 className="text-(--color-text-primary) text-xl font-semibold">
            {product?.name ?? 'Produto'}
          </h1>
          <p className="text-(--color-text-secondary) font-mono text-xs">
            {productId}
          </p>
        </div>
        <Badge variant={PRODUCT_STATUS_BADGE[product?.status ?? status]}>
          {PRODUCT_STATUS_LABELS[product?.status ?? status]}
        </Badge>
      </SurfaceCard>

      {/* Status */}
      <Card title="Status">
        <div className="flex items-end gap-3">
          <div className="max-w-xs flex-1">
            <Select
              label="Alterar status"
              value={status}
              onChange={(e) => setStatus(e.target.value as ProductStatus)}
              options={PRODUCT_STATUSES.map((s) => ({
                value: s,
                label: PRODUCT_STATUS_LABELS[s],
              }))}
            />
          </div>
          <Button
            disabled={!statusDirty || updateStatus.isPending}
            onClick={() => updateStatus.mutate(status)}
          >
            {updateStatus.isPending ? 'Salvando…' : 'Salvar status'}
          </Button>
        </div>
        {updateStatus.error && (
          <p className="text-sm text-red-600">
            {updateStatus.error instanceof Error
              ? updateStatus.error.message
              : 'Falha ao atualizar status'}
          </p>
        )}
      </Card>

      {/* Chave do entregável */}
      <Card title="Chave do entregável">
        {canEditExternalKey ? (
          <>
            <ProductExternalKeyFields
              value={externalKey}
              onChange={setExternalKey}
            />
            <div className="flex justify-end">
              <Button
                disabled={
                  !externalKeyDirty ||
                  !isProductExternalKeyComplete(externalKey) ||
                  updateExternalKey.isPending
                }
                onClick={() =>
                  updateExternalKey.mutate(trimProductExternalKey(externalKey))
                }
              >
                {updateExternalKey.isPending ? 'Salvando…' : 'Salvar chave'}
              </Button>
            </div>
            {updateExternalKey.error && (
              <p className="text-sm text-red-600">
                {updateExternalKey.error instanceof Error
                  ? updateExternalKey.error.message
                  : 'Falha ao atualizar a chave do entregável'}
              </p>
            )}
          </>
        ) : (
          <div className="text-(--color-text-secondary) space-y-1 text-sm">
            <p>
              {product?.provider
                ? CATALOG_PROVIDER_LABELS[product.provider]
                : '—'}
              {' · '}
              <span className="font-mono">{product?.providerAccountId}</span>
            </p>
            <p className="font-mono">
              {product?.productExternalId} × {product?.offerExternalId}
            </p>
            <p className="italic">
              Só é possível alterar a chave enquanto o produto está em “Sem
              configuração”.
            </p>
          </div>
        )}
      </Card>

      {/* Perks associados */}
      <Card
        title={`Perks associados (${perks?.length ?? 0})`}
        action={
          <Button size="sm" onClick={() => setIsPerksEditorOpen(true)}>
            Gerenciar perks
          </Button>
        }
      >
        {perksLoading ? (
          <p className="text-(--color-text-secondary) text-sm">Carregando…</p>
        ) : !perks || perks.length === 0 ? (
          <p className="text-(--color-text-secondary) text-sm italic">
            Nenhum perk associado a este produto.
          </p>
        ) : (
          <ul className="space-y-2">
            {perks.map((pp) => (
              <li
                key={pp.id}
                className="border-(--color-border) flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2.5"
              >
                <div className="space-y-0.5">
                  <p className="text-(--color-text-primary) text-sm">
                    <span className="font-mono">
                      {perkLabelById.get(pp.perkId)?.key ?? pp.perkId}
                    </span>
                    {perkLabelById.has(pp.perkId) && (
                      <span className="text-(--color-text-secondary)">
                        {' · '}
                        {perkLabelById.get(pp.perkId)?.kind}
                      </span>
                    )}
                  </p>
                  <p className="text-(--color-text-secondary) text-xs">
                    {pp.maxUsages != null
                      ? `${pp.maxUsages} uso(s)`
                      : 'usos ilimitados'}
                    {' · '}
                    {pp.durationInMilliseconds != null
                      ? `${msToDays(pp.durationInMilliseconds)} dia(s)`
                      : 'sem prazo'}
                    {pp.renewEveryInMilliseconds != null && (
                      <>
                        {' · '}
                        {`renova a cada ${msToDays(pp.renewEveryInMilliseconds)} dia(s)`}
                      </>
                    )}
                    {' · '}
                    {OVERDUE_POLICY_LABELS[pp.overduePolicy]}
                    {pp.deliversPerCycle && (
                      <>{` · ${DELIVERS_PER_CYCLE_LABELS.true}`}</>
                    )}
                  </p>
                </div>
                {pp.targetScopes.length > 0 && (
                  <ScopeBadgeList ids={pp.targetScopes} />
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {productId && (
        <ProductPerksEditorModal
          productId={productId}
          productName={product?.name ?? 'produto'}
          open={isPerksEditorOpen}
          onClose={() => setIsPerksEditorOpen(false)}
        />
      )}
    </PageContainer>
  );
}
